import { useCallback, useEffect, useState } from "react";
import { BellRing, CalendarClock, FileCheck2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, downloadPdf, fmtDate, inr, waLink } from "@/lib/api";
import { payInfoText } from "@/lib/messages";
import { useAdminSettings } from "@/lib/useAdminSettings";
import { Empty, PageHeader, Panel } from "@/components/admin/common";
import { PaymentsDialog } from "@/components/admin/PaymentsDialog";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";

const WINDOWS = ["7", "15", "30", "60", "90"];

export const reminderText = (b, s) =>
  `Hi ${b.customer_name}, greetings from Ocean Wings Travel! This is a friendly reminder that a balance of ${inr(b.balance_due)} is due for your Andaman trip` +
  `${b.package_name ? ` (${b.package_name})` : ""} on ${fmtDate(b.travel_date)}, booking ${b.booking_no}. ` +
  `Total: ${inr(b.total_price)}, received so far: ${inr(b.advance_paid)}.${payInfoText(s, b.booking_no)}\n\nPlease let us know once the payment is done. Thank you!`;

const DaysBadge = ({ d, testId }) => {
  const [text, cls] = d === null ? ["Date unknown", "bg-slate-100 text-slate-600"]
    : d < 0 ? ["Travel date passed", "bg-rose-100 text-rose-700"]
    : d === 0 ? ["Travels today", "bg-rose-100 text-rose-700"]
    : d <= 7 ? [`${d} day${d === 1 ? "" : "s"} left`, "bg-rose-50 text-rose-700"]
    : d <= 15 ? [`${d} days left`, "bg-amber-50 text-amber-800"] : [`${d} days left`, "bg-sky-50 text-sky-800"];
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid={testId}>{text}</span>;
};

const Row = ({ b, settings, onReminded, onPayments }) => (
  <Panel className="p-5 grid gap-4 md:grid-cols-12 md:items-center" data-testid={`reminder-row-${b.id}`}>
    <div className="md:col-span-4 min-w-0">
      <div className="flex items-center gap-2 flex-wrap"><p className="font-semibold text-navy">{b.customer_name}</p><DaysBadge d={b.days_to_travel} testId={`reminder-days-${b.id}`} /></div>
      <p className="text-xs text-slate-500 truncate">{b.booking_no} · {b.phone} · {b.package_name || "Custom package"}</p>
      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Travel {fmtDate(b.travel_date)}</p>
    </div>
    <div className="md:col-span-4 grid grid-cols-3 gap-2 text-sm">
      <div><p className="text-xs text-slate-500">Total</p><p className="font-mono">{inr(b.total_price)}</p></div>
      <div><p className="text-xs text-slate-500">Received</p><p className="font-mono text-emerald-600">{inr(b.advance_paid)}</p></div>
      <div><p className="text-xs text-slate-500">Balance</p><p className="font-mono font-semibold text-coral" data-testid={`reminder-balance-${b.id}`}>{inr(b.balance_due)}</p></div>
    </div>
    <div className="md:col-span-4 flex flex-col gap-2 md:items-end">
      <div className="flex gap-2 flex-wrap">
        <a href={waLink(b.phone, reminderText(b, settings))} target="_blank" rel="noreferrer" onClick={() => onReminded(b)} data-testid={`reminder-send-${b.id}`}
          className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors">
          <WhatsAppIcon className="h-4 w-4" /> Send reminder
        </a>
        <Button size="icon" variant="outline" className="h-9 w-9 text-emerald-600" title="Record payment" onClick={() => onPayments(b)} data-testid={`reminder-payments-${b.id}`}><Wallet className="h-4 w-4" /></Button>
        <Button size="icon" variant="outline" className="h-9 w-9 text-ocean" title="Booking confirmation PDF" onClick={() => downloadPdf(`/admin/bookings/${b.id}/voucher.pdf`, `${b.booking_no}-confirmation.pdf`)} data-testid={`reminder-voucher-${b.id}`}><FileCheck2 className="h-4 w-4" /></Button>
      </div>
      <p className="text-xs text-slate-500" data-testid={`reminder-last-${b.id}`}>
        {b.last_reminded_at ? `Last reminded ${fmtDate(b.last_reminded_at)} · ${b.reminder_count} time${b.reminder_count === 1 ? "" : "s"}` : "Not reminded yet"}
      </p>
    </div>
  </Panel>
);

export default function Reminders() {
  const settings = useAdminSettings();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);
  const load = useCallback(() => api.get("/admin/reminders", { params: { days } }).then((r) => setData(r.data)), [days]);
  useEffect(() => { load(); }, [load]);

  const reminded = async (b) => {
    const { data: nb } = await api.post(`/admin/bookings/${b.id}/reminded`);
    setData((d) => ({ ...d, bookings: d.bookings.map((x) => (x.id === nb.id ? { ...x, ...nb } : x)) }));
  };

  return (
    <div data-testid="admin-reminders-page">
      <PageHeader title="Payment reminders" subtitle="Confirmed bookings with a balance due, sorted by travel date. Send a WhatsApp reminder in one tap." />
      <div className="grid gap-4 sm:grid-cols-3 mb-4">
        <Panel className="p-5"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Bookings with balance</p><p className="mt-2 font-mono text-2xl font-semibold text-navy" data-testid="reminders-count">{data?.count ?? "-"}</p></Panel>
        <Panel className="p-5"><p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total balance due</p><p className="mt-2 font-mono text-2xl font-semibold text-coral" data-testid="reminders-total-due">{data ? inr(data.total_due) : "-"}</p></Panel>
        <Panel className="p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Travelling within</p>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {WINDOWS.map((w) => (
              <button key={w} onClick={() => setDays(w)} data-testid={`reminders-window-${w}`}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${days === w ? "bg-ocean text-white border-ocean" : "border-slate-200 text-slate-600 hover:border-ocean"}`}>{w} days</button>
            ))}
          </div>
        </Panel>
      </div>
      {!data ? <Skeleton className="h-40 rounded-2xl" /> : data.bookings.length === 0 ? (
        <Panel><Empty testId="reminders-empty"><BellRing className="h-8 w-8 mx-auto mb-3 text-emerald-500" />No balances due for trips in the next {days} days.</Empty></Panel>
      ) : (
        <div className="space-y-3">
          {data.bookings.map((b) => <Row key={b.id} b={b} settings={settings} onReminded={reminded} onPayments={setPaying} />)}
        </div>
      )}
      <p className="mt-4 text-xs text-slate-500">Trips whose travel date has passed but still show a balance are listed first. Mark a booking as completed or record the payment to clear it.</p>
      <PaymentsDialog booking={paying} onClose={() => { setPaying(null); load(); }} onChanged={() => {}} />
    </div>
  );
}
